/**
 * LINE 綁定區塊（#24 Phase B）— 置於個人資料頁。
 * 未綁：「綁定 LINE」→ 取 OAuth URL → 導向 LINE（回來走 /line-callback 完成 TOTP + bind）。
 * 已綁：顯示 LINE 名稱 + 「解除綁定」（需 TOTP）。
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useMessage } from '../contexts/MessageContext';
import { getLineStatus, getAuthorizeUrl, unbindLine, LineBinding } from '../services/lineLoginService';

const LineBindingSection: React.FC = () => {
  const { t } = useTranslation();
  const { showSuccess, showApiError } = useMessage();
  const [binding, setBinding] = useState<LineBinding | null>(null);
  const [unbinding, setUnbinding] = useState(false);
  const [totp, setTotp] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => { getLineStatus().then(setBinding).catch(showApiError); };
  useEffect(() => { load(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  const startBind = async () => {
    try {
      const url = await getAuthorizeUrl();
      window.location.href = url;   // 導向 LINE OAuth；授權後 LINE 導回 backend → /line-callback
    } catch (err) { showApiError(err); }
  };

  const doUnbind = async () => {
    if (totp.length < 6) return;
    setBusy(true);
    try {
      await unbindLine(totp);
      showSuccess('SYS370002', {});
      setUnbinding(false); setTotp('');
      load();
    } catch (err) { showApiError(err); }
    finally { setBusy(false); }
  };

  const btn: React.CSSProperties = { padding: '6px 14px', border: 'none', borderRadius: 4, cursor: 'pointer', color: '#fff', background: '#06c755' };
  const inp: React.CSSProperties = { padding: '6px 8px', border: '1px solid #ccc', borderRadius: 4, width: 160 };

  return (
    <div className="form-section" style={{ marginTop: 16 }}>
      <h3>{t('lineLogin.title', 'LINE 綁定')}</h3>
      {!binding ? (
        <div style={{ color: '#999' }}>{t('common.loading', '載入中...')}</div>
      ) : binding.bound ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {binding.line_picture_url && (
            <img src={binding.line_picture_url} alt="line" style={{ width: 36, height: 36, borderRadius: '50%' }} />
          )}
          <span>{t('lineLogin.boundAs', { name: binding.line_display_name || binding.line_user_id, defaultValue: `已綁定：${binding.line_display_name || ''}` })}</span>
          {!unbinding ? (
            <button style={{ ...btn, background: '#d32f2f' }} onClick={() => setUnbinding(true)}>
              {t('lineLogin.unbindButton', '解除綁定')}
            </button>
          ) : (
            <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
              <input style={inp} placeholder={t('lineLogin.totpLabel', '請輸入兩階段驗證碼')}
                value={totp} onChange={(e) => setTotp(e.target.value.trim())} />
              <button style={{ ...btn, background: '#d32f2f' }} disabled={busy} onClick={doUnbind}>
                {t('lineLogin.unbindButton', '解除綁定')}
              </button>
              <button style={{ ...btn, background: '#888' }} onClick={() => { setUnbinding(false); setTotp(''); }}>
                {t('common.cancel', '取消')}
              </button>
            </span>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ color: '#666' }}>{t('lineLogin.notBound', '尚未綁定 LINE')}</span>
          <button style={btn} onClick={startBind}>{t('lineLogin.bindButton', '綁定 LINE')}</button>
        </div>
      )}
    </div>
  );
};

export default LineBindingSection;
